/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import Copy16 from '@carbon/icons-react/es/copy/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import { Typography } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Menu as DropdownMenu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { SubMenu as ViewSubMenu } from 'components/molecules/views/saved-views/components';
import {
  Empty,
  Loader,
} from 'components/organisms/projects/project-sidebar/spaces-menu/styles';

const StyledMenuItem = styled(DropdownMenu.Item)`
  color: var(--gray-80);
`;

const Name = styled(Typography.Paragraph).attrs({
  ellipsis: {
    rows: 1,
    tooltip: true,
  },
})`
  margin-bottom: 0;
`;

const Wrapper = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  > span {
    display: flex;
  }
  ${Name} {
    width: calc(100% - 32px);
  }
`;

const EmptyWrapper = styled(Empty)`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

export enum ItemGroupHeader {
  SavedItems = 'SavedItems',
  SharedItems = 'SharedItems',
  SearchResult = 'SearchResult',
}

export interface MenuItem<ItemDef> {
  item: ItemDef;
  name: string;
  icon: JSX.Element | null;
  id: number;
  shouldShowMenu: boolean;
  contextMenu: Array<ContextMenuKeys>;
}

export enum ContextMenuKeys {
  Edit = 'edit',
  Duplicate = 'duplicate',
  Delete = 'delete',
}
interface Props<ItemDef> {
  menuItems: MenuItem<ItemDef>[];
  onEdit: (def: ItemDef) => void;
  onDelete: (def: ItemDef) => void;
  onClick: (def: ItemDef) => void;
  onDuplicate: (def: ItemDef) => void;
  loading: boolean;
  label: JSX.Element | string;
  key: ItemGroupHeader;
  icon: JSX.Element;
}

export const renderSubMenu = <ItemDef,>({
  menuItems,
  onEdit,
  onDelete,
  onClick,
  onDuplicate,
  loading,
  label,
  key,
  icon: mainIcon,
}: Props<ItemDef>): JSX.Element => {
  return (
    <ViewSubMenu icon={mainIcon} title={label} key={key}>
      {loading ? (
        <Loader>
          <FormattedMessage id="loading" />
        </Loader>
      ) : menuItems.length > 0 ? (
        menuItems.map(
          ({ item, name, icon, id, shouldShowMenu, contextMenu }) => (
            <StyledMenuItem icon={icon} key={id} onClick={() => onClick(item)}>
              <Wrapper>
                <Name>{name}</Name>
                {shouldShowMenu ? (
                  <span onClick={(e) => e.stopPropagation()}>
                    <MoreActionsDropdown
                      className="more-actions"
                      placement="bottomLeft"
                      menu={
                        <DropdownMenu>
                          {contextMenu.includes(ContextMenuKeys.Edit) && (
                            <DropdownMenu.Item
                              key="edit"
                              icon={<Edit16 />}
                              onClick={() => onEdit(item)}
                            >
                              <FormattedMessage id="edit" />
                            </DropdownMenu.Item>
                          )}
                          {contextMenu.includes(ContextMenuKeys.Duplicate) && (
                            <DropdownMenu.Item
                              key="duplicate"
                              icon={<Copy16 />}
                              onClick={() => onDuplicate(item)}
                            >
                              <FormattedMessage id="duplicate" />
                            </DropdownMenu.Item>
                          )}
                          {contextMenu.includes(ContextMenuKeys.Delete) && (
                            <DropdownMenu.ItemDelete
                              key="delete"
                              onClick={() => onDelete(item)}
                            />
                          )}
                        </DropdownMenu>
                      }
                    />
                  </span>
                ) : null}
              </Wrapper>
            </StyledMenuItem>
          ),
        )
      ) : (
        <EmptyWrapper>
          <FormattedMessage id="dashboard.slider.empty" />
        </EmptyWrapper>
      )}
    </ViewSubMenu>
  );
};

interface RenderDefaultOptionsProps<ItemDef> {
  menuItem: MenuItem<ItemDef>;
  onEdit: (def: ItemDef) => void;
  onClick: (def: ItemDef) => void;
  onDuplicate: (def: ItemDef) => void;
  loading: boolean;
}

const MainMenuWrapper = styled(Wrapper)`
  font-weight: initial;
  svg {
    margin-right: var(--layout-01);
  }
  .more-actions {
    opacity: 0;
  }
  &:hover {
    .more-actions {
      opacity: 1;
    }
  }
`;

export const renderDefaultOptions = <ItemDef,>({
  menuItem: { icon, id, item, name },
  onEdit,
  onClick,
  onDuplicate,
  loading,
}: RenderDefaultOptionsProps<ItemDef>): JSX.Element => {
  return (
    <>
      {loading ? (
        <Loader>
          <FormattedMessage id="loading" />
        </Loader>
      ) : (
        <StyledMenuItem onClick={() => onClick(item)} key={id}>
          <MainMenuWrapper>
            {icon}
            <Name>{name}</Name>
            <span onClick={(e) => e.stopPropagation()}>
              <MoreActionsDropdown
                className="more-actions"
                placement="bottomLeft"
                menu={
                  <DropdownMenu>
                    <DropdownMenu.Item
                      key="edit"
                      icon={<Edit16 />}
                      onClick={() => onEdit(item)}
                    >
                      <FormattedMessage id="edit" />
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      key="duplicate"
                      icon={<Copy16 />}
                      onClick={() => onDuplicate(item)}
                    >
                      <FormattedMessage id="duplicate" />
                    </DropdownMenu.Item>
                  </DropdownMenu>
                }
              />
            </span>
          </MainMenuWrapper>
        </StyledMenuItem>
      )}
    </>
  );
};
